import {register} from '../vendor/theme-scripts/theme-sections';
import {popoutSection} from '../features/popout';
import debounce from '../util/debounce';
import {swatchGridSection, makeGridSwatches, swatchSection, Swatch} from '../features/swatch';
import {quickviewSection, Quickview} from '../features/quickview';
import {ajaxify, Ajaxify} from '../features/infinity-scroll';
import {RangeSlider} from '../features/range-slider';

const sections = {};

const selectors = {
  accordionElements: 'accordion-elements',
  collectionGridWrapper: '[data-collection-grid-wrapper]',
  collectionProducts: '[data-collection-products]',
  collectionSort: '[data-collection-sort]',
  collectionWrapper: '[data-collection-wrapper]',
  filterContainer: '[data-filter-container]',
  filterTitle: '[data-accordion-trigger]',
  filters: '[data-filters]',
  filtersResets: '[data-filters-reset]',
  filtersResetButton: '[data-filters-reset-button]',
  filtersForm: '[data-filters-form]',
  filtersResetButtons: '[data-filter-reset-button]',
  inputs: 'input, select, label, textarea',
  pagination: '[data-pagination]',
  priceMin: '[data-field-price-min]',
  priceMax: '[data-field-price-max]',
  rangeMin: '[data-se-min-value]',
  rangeMax: '[data-se-max-value]',
  rangeSlider: '[data-range-slider]',
  swatch: '[data-swatch]',
};

const classes = {
  filtersTop: 'collection__filters--top',
  isLoading: 'is-loading',
};

const attributes = {
  single: 'single',
  sort: 'data-sort',
  dataCollection: 'data-collection',
  filtersEnable: 'data-filters-enable',
  filtersAvailable: 'data-filters-available',
  rangeMinValue: 'data-se-min-value',
  rangeMaxValue: 'data-se-max-value',
};

class Collection {
  constructor(section) {
    this.container = section.container;
    this.collectionGridWrapper = this.container.querySelector(selectors.collectionGridWrapper);
    this.collectionSort = this.container.querySelector(selectors.collectionSort);
    this.collectionProducts = this.container.querySelector(selectors.collectionProducts);
    this.collectionWrapper = this.container.querySelector(selectors.collectionWrapper);
    this.pagination = this.container.querySelector(selectors.pagination);
    this.accordionElements = this.container.querySelector(selectors.accordionElements);
    this.filters = this.container.querySelector(selectors.filters);
    this.filtersEnable = this.container.getAttribute(attributes.filtersEnable) === 'true';
    this.filtersForm = this.container.querySelector(selectors.filtersForm);
    this.filtersResets = this.container.querySelector(selectors.filtersResets);
    this.filtersInputs = [];
    this.sort = null;
    this.collection = null;
    this.reset = null;
    this.filtersPositionState = this.isTopPosition() ? 'top' : 'left';
    this.isRangeSliderInitialized = false;
    this.resizeFiltersEvent = () => this.checkFiltersPosition();
    this.documentClick = (e) => this.closeOnOutsideClick(e);

    this.init();
  }

  init() {
    if (this.collectionSort) {
      this.initSort();
    }

    if (this.collectionProducts) {
      this.collection = this.collectionProducts.getAttribute(attributes.dataCollection);
    }

    if (this.filtersEnable) {
      this.initFacetedFilters();
    }

    this.initFilterToggleButtons();
    this.checkFiltersPosition(true);
    document.addEventListener('theme:resize', this.resizeFiltersEvent);
  }

  initSort() {
    this.collectionSort.addEventListener('change', (evt) => {
      const url = new window.URL(window.location.href);
      const value = evt.currentTarget.value;
      const params = url.searchParams;
      params.set('sort_by', value);
      url.search = params.toString();
      this.collectionProducts.setAttribute(attributes.sort, value);
      this.requestFilteredProducts(url.toString());
    });
  }

  initFilterToggleButtons() {
    if (!this.filters) {
      return;
    }

    this.filters.querySelectorAll(selectors.filterTitle)?.forEach((button) => {
      button.addEventListener('click', (e) => {
        const distanceFromTop = button.parentNode.querySelector(selectors.filterContainer).getBoundingClientRect().top;
        document.documentElement.style.setProperty('--filters-top', `${distanceFromTop}px`);

        const isPriceFilter = button.parentNode.querySelector(selectors.rangeSlider);

        if (isPriceFilter && !this.isRangeSliderInitialized) {
          this.filtersForm.dispatchEvent(new CustomEvent('theme:filter:init', {bubbles: true}));
          this.isRangeSliderInitialized = true;
        }

        // Close horizontal filters on click outside their container
        document.addEventListener('click', this.documentClick);
      });
    });
  }

  initFacetedFilters() {
    if (!this.filters.hasAttribute(attributes.filtersAvailable)) {
      return;
    }

    new RangeSlider(this.filtersForm);
    this.filtersInputs = this.filtersForm.querySelectorAll(selectors.inputs);

    if (this.filtersInputs.length) {
      this.filtersInputs.forEach((el) => {
        el.addEventListener(
          'input',
          debounce(() => {
            if (this.filtersForm && typeof this.filtersForm.submit === 'function') {
              this.submitForm();
            }
          }, 500)
        );
      });
    }

    this.filtersForm.addEventListener('theme:filter:range-update', () => this.updateRange());
    this.initResets();
  }

  submitForm() {
    const formData = new FormData(this.filtersForm);
    const search = new URLSearchParams(formData);
    this.requestFilteredProducts(`${this.collection}${search.toString()}`);
  }

  initResets() {
    const resets = this.container.querySelectorAll(selectors.filtersResetButtons);
    this.resetAlt = this.collectionProducts.querySelector(selectors.filtersResetButton);

    // Bind reset
    if (resets.length) {
      resets.forEach((button) => {
        button.addEventListener('click', (e) => this.bindResetButton(e));
      });
    }
    if (this.resetAlt) {
      this.resetAlt.addEventListener('click', (e) => this.bindResetButton(e));
    }
  }

  updateRange() {
    if (this.filtersForm && typeof this.filtersForm.submit === 'function') {
      const rangeMin = this.filtersForm.querySelector(selectors.rangeMin);
      const rangeMax = this.filtersForm.querySelector(selectors.rangeMax);
      const priceMin = this.filtersForm.querySelector(selectors.priceMin);
      const priceMax = this.filtersForm.querySelector(selectors.priceMax);
      const checkElements = rangeMin && rangeMax && priceMin && priceMax;

      if (checkElements && rangeMin.hasAttribute(attributes.rangeMinValue) && rangeMax.hasAttribute(attributes.rangeMaxValue)) {
        const priceMinValue = parseInt(priceMin.placeholder);
        const priceMaxValue = parseInt(priceMax.placeholder);
        const rangeMinValue = parseInt(rangeMin.getAttribute(attributes.rangeMinValue));
        const rangeMaxValue = parseInt(rangeMax.getAttribute(attributes.rangeMaxValue));

        if (priceMinValue !== rangeMinValue || priceMaxValue !== rangeMaxValue) {
          priceMin.value = rangeMinValue;
          priceMax.value = rangeMaxValue;

          this.submitForm();
        }
      }
    }
  }

  closeOnOutsideClick(e) {
    const isFiltersContainer = this.filters.contains(e.target);
    const isFilterExpanded = this.filters.querySelector('details[open]');

    // Close filter
    if (!isFiltersContainer && isFilterExpanded && this.isTopPosition()) {
      const expandedFilter = this.filters.querySelector('details[open]');
      expandedFilter.querySelector(selectors.filterTitle).dispatchEvent(new Event('click'));

      document.removeEventListener('click', this.documentClick);
    }
  }

  requestFilteredProducts(url) {
    const collectionWrapperTop = parseInt(Math.ceil(this.collectionWrapper.offsetTop) - theme.dimensions.headerScrolled);

    this.collectionWrapper.classList.add(classes.isLoading);

    // Scroll back to top
    window.scrollTo({
      top: collectionWrapperTop,
      left: 0,
      behavior: 'smooth',
    });

    if (history.replaceState) {
      window.history.pushState({path: url}, '', url);
    }

    fetch(url)
      .then((response) => {
        return response.text();
      })
      .then((data) => {
        const createdElement = document.createElement('div');
        createdElement.innerHTML = data;
        const collectionProducts = createdElement.querySelector(selectors.collectionProducts).innerHTML;
        const pagination = createdElement.querySelector(selectors.pagination);
        const filters = createdElement.querySelector(selectors.filters);

        this.collectionProducts.innerHTML = collectionProducts;

        if (this.pagination) {
          this.pagination.innerHTML = pagination !== null ? pagination.innerHTML : '';
        }

        if (!this.pagination && pagination !== null) {
          this.collectionGridWrapper.appendChild(pagination);
          this.pagination = this.collectionGridWrapper.querySelector(selectors.pagination);
        }

        if (filters) {
          const filtersHTML = filters.innerHTML;

          this.filters.innerHTML = filtersHTML;
          this.filtersForm = this.filters.querySelector(selectors.filtersForm);
          this.accordionElements = this.container.querySelector(selectors.accordionElements);

          this.initFacetedFilters();
          this.checkFiltersPosition();
          this.initFilterToggleButtons();
          this.bindSwatchFilters();
        }

        ajaxify.onUnload();
        new Ajaxify(this.container);
        makeGridSwatches(this.container);
        new Quickview(this.container);
      })
      .catch((e) => {
        this.collectionWrapper.classList.remove(classes.isLoading);
      })
      .finally(() => {
        // Stop loading animation
        setTimeout(() => {
          this.collectionWrapper.classList.remove(classes.isLoading);
        }, 450);
      });
  }

  checkFiltersPosition(init = false) {
    if (!this.filters) {
      return;
    }

    const currentState = this.isTopPosition() ? 'top' : 'left';
    const stateChanged = this.filtersPositionState !== currentState;

    if (!init && !stateChanged) return;

    // Run only if filters position is set to "Top" or window width is < 1279
    if (this.isTopPosition()) {
      this.accordionElements?.setAttribute(attributes.single, true);

      // Close filters if position is set to "Top"
      this.filters.querySelectorAll('details[open]')?.forEach((filter) => {
        const filterExpanded = filter.hasAttribute('open');

        // Check if dropdown is expanded and close it
        if (filterExpanded) {
          filter.removeAttribute('open');
          filter.querySelector(selectors.filterContainer).style.height = 0;
        }
      });
    } else {
      this.accordionElements?.removeAttribute(attributes.single);
    }

    // Update filters state
    this.filtersPositionState = currentState;
  }

  isTopPosition() {
    return window.innerWidth < theme.sizes.widescreen || this.filters.classList.contains(classes.filtersTop);
  }

  bindSwatchFilters() {
    this.swatches = [];
    const els = this.container.querySelectorAll(selectors.swatch);
    els.forEach((el) => {
      this.swatches.push(new Swatch(el));
    });
  }

  bindResetButton(e) {
    e.preventDefault();

    this.requestFilteredProducts(e.currentTarget.href);
  }

  onUnload() {
    document.removeEventListener('theme:resize', this.resizeFiltersEvent);
  }
}

const CollectionSection = {
  onLoad() {
    sections[this.id] = new Collection(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

register('collection', [CollectionSection, quickviewSection, ajaxify, swatchGridSection, swatchSection, popoutSection]);
register('search-template', [CollectionSection, quickviewSection, ajaxify, swatchGridSection, swatchSection, popoutSection]);
